// 手机端让表单、记录与操作自然换行，移除固定最小宽度；业务提交与原有权限保持一致。
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import confetti from "canvas-confetti";
import { chunkUpload } from "../components/EnginePanel";
import { CheckCircle2, Camera, ExternalLink, Image as ImageIcon, Loader2, Paperclip, RefreshCw, Sun, Upload } from "lucide-react";
import { EvidenceLightbox } from "../components/EvidenceLightbox";
import { CompleteNoteModal } from "../components/CompleteNoteModal";
import { fmtD } from "../lib/date";

const STATE_CHIP = {
  ready: ["可执行", "bg-emerald-100 text-emerald-700"],
  blocked: ["被阻塞", "bg-red-100 text-red-700"],
  waiting_external: ["等外部", "bg-blue-100 text-blue-700"],
  waiting_time: ["待时间", "bg-amber-100 text-amber-700"],
};

export default function MyToday() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  const [previewEv, setPreviewEv] = useState(null);
  const [noteItem, setNoteItem] = useState(null);
  const upItemRef = useRef(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = async () => {
    try { const { data: d } = await api.get("/engine-trace/my-today"); setData(d); setDoneIds([]); } catch { setData({ items: [] }); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const items = data?.items || [];
  const finishAnim = (it) => {
    setDoneIds((d) => [...d, it.taskId]);
    const remaining = items.filter((x) => x.taskId !== it.taskId && !doneIds.includes(x.taskId)).length;
    if (remaining === 0) {
      confetti({ particleCount: 180, spread: 80, origin: { y: 0.7 } });
      setTimeout(() => confetti({ particleCount: 90, spread: 120, origin: { y: 0.5 } }), 350);
      toast.success(zh ? "🎉 今日必办全部完成！辛苦了" : "🎉 All done for today!");
    }
    setTimeout(load, 1000);
  };

  const complete = async (it, note, audio) => {
    setBusyId(it.taskId);
    try {
      const { data: r } = await api.post(`/projects/${it.projectId}/engine/tasks/${it.taskId}/complete`,
        { ...(note ? { note } : {}), ...(audio ? { audioFileId: audio.fileId, durationSec: audio.durationSec } : {}) });
      toast.success(zh ? "任务已完成 ✓" : "Done ✓");
      if (r?.advanced) toast.success(zh ? `🚀 已自动推进至「${r.advanced.toName}」` : `Advanced`, { duration: 5000 });
      setNoteItem(null);
      finishAnim(it);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "完成失败" : "Failed")); }
    setBusyId(null);
  };

  const pickUpload = (it, cam) => { upItemRef.current = it; (cam ? camRef : fileRef).current?.click(); };
  const uploadComplete = async (files) => {
    const it = upItemRef.current;
    if (!it || !files.length) return;
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) { toast.error(zh ? `「${f.name}」超过 25MB 上限` : "Max 25MB per file"); return; }
    }
    setBusyId(it.taskId);
    const progId = files.length > 1 ? toast.loading(zh ? `上传凭证 1/${files.length}…` : `Uploading 1/${files.length}…`) : null;
    try {
      for (let i = 0; i < files.length; i++) {
        if (progId) toast.loading(zh ? `上传凭证 ${i + 1}/${files.length}：${files[i].name}` : `Uploading ${i + 1}/${files.length}`, { id: progId });
        const up = await chunkUpload(api, files[i]);
        await api.post(`/projects/${it.projectId}/engine/tasks/${it.taskId}/evidence`, up);
      }
      if (progId) toast.dismiss(progId);
      const names = files.map((f) => f.name).join("、").slice(0, 260);
      const { data: r } = await api.post(`/projects/${it.projectId}/engine/tasks/${it.taskId}/complete`, { note: `上传凭证完成：${names}` });
      toast.success(zh ? `${files.length} 份凭证已归档到项目证据，任务自动完成 ✓` : "Uploaded & completed ✓");
      if (r?.advanced) toast.success(zh ? `🚀 已自动推进至「${r.advanced.toName}」` : `Advanced`, { duration: 5000 });
      finishAnim(it);
    } catch (e) {
      if (progId) toast.dismiss(progId);
      toast.error(e?.response?.data?.detail || (zh ? "上传或完成失败" : "Upload/complete failed"));
      load();
    }
    setBusyId(null);
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  const KINDS = [["primary", zh ? "主办项目" : "Primary projects", "text-indigo-700 bg-indigo-50 border-indigo-100"], ["co", zh ? "协办项目" : "Co-assist projects", "text-violet-700 bg-violet-50 border-violet-100"], ["other", zh ? "其他指派" : "Other assignments", "text-slate-600 bg-slate-50 border-slate-200"]];
  const sections = KINDS.map(([k, label, cls]) => {
    const its = items.filter((it) => (it.myKind || "other") === k);
    return [k, label, cls, its.reduce((m, it) => { (m[it.projectId] = m[it.projectId] || []).push(it); return m; }, {})];
  }).filter(([, , , byProj]) => Object.keys(byProj).length > 0);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="my-today-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Sun className="h-5 w-5 text-amber-500" />{zh ? "我的今日必办" : "My Today"}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{fmtD(data.date)} · {zh ? `全部项目中指派给我的当日任务 ${items.length} 项 · 可上传照片/文件作为完成凭证` : `${items.length} tasks assigned to me`}</p>
        </div>
        <button data-testid="my-today-refresh" onClick={() => { setData(null); load(); }} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {items.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400" data-testid="my-today-empty">
          <Sun className="h-10 w-10 mx-auto mb-2 text-amber-300" />
          <p className="text-sm">{zh ? "今日没有指派给你的必办任务 🎉" : "Nothing due today 🎉"}</p>
        </div>
      )}

      {sections.map(([kind, label, cls, byProj]) => (
      <section key={kind} className="space-y-3" data-testid={`my-today-kind-${kind}`}>
        <p className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${cls}`} data-testid={`my-today-kind-label-${kind}`}>{label}<span className="font-normal opacity-70"> · {Object.values(byProj).reduce((n, a) => n + a.length, 0)}</span></p>
      {Object.entries(byProj).map(([pid, its]) => (
        <div key={pid} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`my-today-proj-${pid}`}>
          <button onClick={() => nav(`/project/${pid}`)} className="text-sm font-bold text-slate-800 hover:text-indigo-600 flex items-center gap-1.5 mb-2">
            {its[0].address}<ExternalLink className="h-3.5 w-3.5 text-slate-300" />
          </button>
          <div className="space-y-1.5">
            {its.map((it) => {
              const [chip, chipCls] = STATE_CHIP[it.state] || [it.state, "bg-slate-100 text-slate-500"];
              const needEv = it.requiresEvidence && it.evidenceCount === 0;
              const canDone = it.state === "ready" && !needEv;
              const struck = doneIds.includes(it.taskId);
              return (
                <div key={it.taskId} className={`flex flex-wrap items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 transition-all ${struck ? "opacity-50" : ""}`} data-testid={`my-today-item-${it.taskId}`}>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${chipCls}`}>{zh ? chip : it.state}</span>
                  <div className="basis-full sm:basis-auto flex-1 min-w-0">
                    <p className={`text-[13px] font-medium break-words sm:truncate transition-all ${struck ? "line-through text-emerald-600" : "text-slate-800"}`}>{it.title}</p>
                    <p className="text-[10px] text-slate-400 break-words sm:truncate">{it.subName || ""}{it.dueDate ? ` · ${zh ? "截止" : "due"} ${fmtD(it.dueDate)}` : ""}{it.requiresEvidence ? ` · 📎${it.evidenceCount}` : ""}</p>
                  </div>
                  <button data-testid={`my-today-upload-${it.taskId}`} disabled={busyId === it.taskId} onClick={() => pickUpload(it)}
                    title={zh ? "上传照片/文件凭证，归档到项目证据后自动完成" : "Upload proof to complete"}
                    className="shrink-0 text-[11px] font-semibold rounded-lg px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1">
                    {busyId === it.taskId ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{zh ? "传凭证" : "Upload"}</button>
                  <button data-testid={`my-today-camera-${it.taskId}`} disabled={busyId === it.taskId} onClick={() => pickUpload(it, true)}
                    title={zh ? "手机直接拍照上传，即传即完成" : "Take photo to complete"}
                    className="shrink-0 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 bg-slate-800 text-white hover:bg-slate-700 inline-flex items-center"><Camera className="h-3 w-3" /></button>
                  {canDone ? (
                    <button data-testid={`my-today-done-${it.taskId}`} disabled={busyId === it.taskId} onClick={() => setNoteItem(it)}
                      className="shrink-0 text-[11px] font-semibold rounded-lg px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />{zh ? "完成" : "Done"}</button>
                  ) : !needEv ? (
                    <button data-testid={`my-today-open-${it.taskId}`} onClick={() => nav(`/project/${pid}`)}
                      className="shrink-0 text-[11px] font-semibold rounded-lg px-3 py-1.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "去处理" : "Open"}</button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      </section>
      ))}
      {(data.done || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-emerald-100 p-4" data-testid="my-today-done-section">
          <p className="text-sm font-bold text-emerald-700 mb-2">✓ {zh ? `今日已完成 ${(data.done || []).length} 项（点凭证可直接查看）` : `Done today (${(data.done || []).length})`}</p>
          <div className="space-y-1.5">
            {(data.done || []).map((it) => (
              <div key={it.taskId} className="flex flex-wrap items-center gap-2.5 rounded-xl bg-emerald-50/50 border border-emerald-50 px-3 py-2" data-testid={`my-today-done-item-${it.taskId}`}>
                <div className="basis-full sm:basis-auto flex-1 min-w-0">
                  <p className="text-[13px] text-slate-400 line-through truncate" title={it.title}>{it.title}</p>
                  <p className="text-[10px] text-slate-400 break-words sm:truncate">{it.address}{it.doneAt ? ` · ${String(it.doneAt).slice(11, 16)}` : ""}{it.doneNote ? ` · 💬 ${it.doneNote}` : ""}</p>
                </div>
                <span className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                  {(it.evidence || []).slice(0, 4).map((e, x) => (
                    <button key={x} data-testid={`my-today-ev-${it.taskId}-${x}`} onClick={() => setPreviewEv(e)} title={e.filename}
                      className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 inline-flex items-center gap-0.5">
                      {(e.contentType || "").startsWith("image") ? <ImageIcon className="h-2.5 w-2.5" /> : <Paperclip className="h-2.5 w-2.5" />}
                      {zh ? "凭证" : "file"}{(it.evidence || []).length > 1 ? x + 1 : ""}</button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" data-testid="my-today-upload-input"
        onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) uploadComplete(fs); }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="my-today-camera-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadComplete([f]); }} />
      <EvidenceLightbox ev={previewEv} zh={zh} onClose={() => setPreviewEv(null)} />
      {noteItem && <CompleteNoteModal title={noteItem.title} zh={zh} api={api}
        onConfirm={(note, audio) => complete(noteItem, note, audio)} onClose={() => setNoteItem(null)} />}
    </div>
  );
}
